import {AfterViewInit, Component, OnInit, ViewChild, ViewContainerRef, inject, DestroyRef, ElementRef} from '@angular/core';
import {SidenavService} from '../services/util/sidenav.service';
import {MatSidenav} from '@angular/material/sidenav';
import {ScrollService} from '../services/util/scroll.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take, filter } from 'rxjs';
import {AuthService} from '../services/auth/auth.service';
import {TourService} from '../services/util/tour.service';
import {SessionManagementService} from '../services/util/session-management.service';
import {FabricService} from '../services/fabric/fabric.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { ToastrService } from 'ngx-toastr';
import { CustomToastrService } from '../services/util/toastr.service';
import { ToastrErrorService } from '../services/util/toastr-error.service';
import {RouterSegments} from 'src/app/models/router-segments';
import { translate } from '@jsverse/transloco';
import { PermissionService } from '../services/api/permission.service';
import { PermissionValues } from '../models/permission-values';
import { FeatureService } from '../services/api/feature.service';
import { CloudProvider } from '../models/cloud-provider/cloud-provider';
import { InstancesService } from '../services/api/instances.service';
import { DisasterRecoveryService } from '../services/api/disaster-recovery.service';
import { BlueprintsService } from '../services/api/blueprints.service';

@Component({
    selector: 'app-tenant-layout',
    templateUrl: './tenant-layout.component.html',
    styleUrls: ['./tenant-layout.component.scss'],
    standalone: false
})
export class TenantLayoutComponent  implements OnInit, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  @ViewChild('contentArea', { read: ElementRef })
  contentArea!: ElementRef;

  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('rightSidenav', {static: true}) private rightSidenav: MatSidenav;
  @ViewChild('container', {static: true, read: ViewContainerRef}) private viewContainerRef: ViewContainerRef;
  @ViewChild('scrollableContainer') private scrollableContainer;
  isEnterprise: boolean;
  verticalMenuItems: any;
  tenantsList: any[] = [];
  tenant: any;
  RouterSegments = RouterSegments;
  tenantId: any;
  selectedTenantId: string;
  showLoader = false;
  isAWS: boolean;
  drInProgress$: any;
  drInstance$: any;
  isDREnabled$: any;
  drValidated$: any;
  isDisasterRecoveryPage = false;
  private lastValidatedInstanceId: string | null = null;

  constructor(public sidenavService: SidenavService, private scrollService: ScrollService, private activatedRoute: ActivatedRoute,
              private router: Router, private authService: AuthService, private tourService: TourService, private sessionService: SessionManagementService,
              private fabricService: FabricService, private tenantService: TenantService, private toastrService: CustomToastrService, private toastrErrorService: ToastrErrorService,
              private permissionsService: PermissionService, public featureService: FeatureService, private instanceService: InstancesService,
              private blueprintsService: BlueprintsService, private disasterRecoveryService: DisasterRecoveryService) {
  }

  ngOnInit(): void {
    let tenantId;
    let url = window.location.pathname;
    let urlArray = url.split('/');
    if(urlArray.length >= 4) {
      tenantId = urlArray[3];
    } else {
      tenantId = '';
    }
    if (tenantId && tenantId !== 'error' && tenantId !== 'my-account') {
      this.selectedTenantId = tenantId;
    } else if (tenantId === 'error') {
      this.selectedTenantId = window.location.href.split('/')[5];
    }

    this.resetDRValidation();
    this.disasterRecoveryService.clearDrStatus();
    this.drInProgress$ = this.disasterRecoveryService.drInProgress$;
    this.drInstance$ = this.disasterRecoveryService.drInstance$;
    this.isDREnabled$ = this.disasterRecoveryService.isDREnabled$;
    this.drValidated$ = this.disasterRecoveryService.drValidated$;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: NavigationEnd) => {
      this.isDisasterRecoveryPage = event.url.includes('/disaster-recovery');
    });

    if (this.selectedTenantId && this.authService.scope === '/global') {
      this.showLoader = true;
      this.checkDREnabled(this.selectedTenantId);
      let scope = '';
      scope = PermissionValues.TENANT + this.selectedTenantId;
      this.authService.scope = scope;
      this.permissionsService.getAuthAssignedPermissions(scope)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
        this.showLoader = false;
        this.authService.assignedPermissions = response.permissions;
      },
      (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    } else {
      if (this.selectedTenantId && this.authService.scope !== '/global') {
        this.getAssignedFeatures(tenantId);
      }
    }
    this.tenantService.tenantData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
      this.isAWS = data === CloudProvider.AWS ? true : false;
    });
    this.tenant = this.authService.principal.provider;
    this.sidenavService.setRightSidenav(this.rightSidenav);
    this.sidenavService.setViewContainerRef(this.viewContainerRef);
    this.addNavigationHandler();
    // this.startSessionMonitoring();
    this.getTenantsList();

    this.scrollService.scrollTop$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
      this.scrollToTop();
    });
    this.setupDRBlueprintValidation();
    this.disasterRecoveryService.drInProgress$
      .pipe(
        filter(isInProgress => isInProgress),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.resetValidationCache();
      });
  }

  scrollToTop() {
    this.contentArea.nativeElement.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }

  getAssignedFeatures(tenantId): void {
    this.featureService.getTenantIdFeatures(tenantId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((features: any) => {
      this.authService.assignedFeaturePermissions = features;
    },(error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  private checkDREnabled(tenantId: string): void {
    this.tenantService.getTenants(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any) => {
        const isEnabled = !(response[0]?.parameters?.hasSecondaryRegion === 'false');
        this.disasterRecoveryService.setDREnabled(isEnabled);
      });
  }

  mouseEnter(): void {
    if (!this.sidenavService.isExpanded) {
      this.sidenavService.isShowing = true;
    }
  }

  mouseLeave(): void {
    if (!this.sidenavService.isExpanded) {
      this.sidenavService.isShowing = false;
    }
  }

  ngAfterViewInit(): void {
    this.scrollService.setScrollableContainer(this.scrollableContainer.elementRef.nativeElement);
  }

  scroll($event: Event): void {
    this.scrollService.publishScrollEvent($event);
  }

  getTenantsList(): void {
    this.tenantService.getTenants()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.tenantsList = data;
          this.tenantsList = data.sort((a, b) => (a.tenantId.toLowerCase() < b.tenantId.toLowerCase()) ? -1 : 1);
        }
      },
      (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  selectedTenant(tenant: string): void {
    this.showLoader = true;
    this.tenantId = tenant;
    this.resetDRValidation();
    this.disasterRecoveryService.clearDrStatus();
    this.drInProgress$ = this.disasterRecoveryService.drInProgress$;
    this.drInstance$ = this.disasterRecoveryService.drInstance$;
    this.isDREnabled$ = this.disasterRecoveryService.isDREnabled$;
    this.drValidated$ = this.disasterRecoveryService.drValidated$;
    this.checkDREnabled(this.tenantId);
    let scope = '/global';
    if (this.tenantId) {
      scope = PermissionValues.TENANT + this.tenantId;
    }
    this.authService.scope = scope;
    this.permissionsService.getAuthAssignedPermissions(scope)
    .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.showLoader = false;
      this.authService.assignedPermissions = response.permissions;
      this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId]);
    },
    (error) => {
        this.showLoader = false;
        const apiError = error.error;
        if (error.status === 404 && apiError?.error?.code === '10004') {
          this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, 'service-error'], { skipLocationChange: true });
          return;
        }
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
    this.getAssignedFeatures(this.tenantId);
  }

  private startSessionMonitoring(): void {
    this.sessionService.expirationEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(expired => {
        if (expired) {
          this.fabricService.getClient().isAnonymousProvider = true;
          this.authService.logout();
        }
      });
  }

  private addNavigationHandler(): void {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((evt) => {
        if (!(evt instanceof NavigationEnd)) {
          return;
        }
        this.sidenavService.closeRightSidenav();
        this.scrollService.scrollTo(0);
        this.tourService.hideTour();
      });
  }

  private resetDRValidation(): void {
    this.lastValidatedInstanceId = null;
    this.disasterRecoveryService.resetDrValidated();
  }

  private resetValidationState(): void {
    this.lastValidatedInstanceId = null;
  }

  resetValidationCache(): void {
    this.lastValidatedInstanceId = null;
  }

  private setupDRBlueprintValidation(): void {
    this.disasterRecoveryService.drInstance$
      .pipe(
        filter(drInstance => !!drInstance),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(drInstance => {
        if (drInstance.id !== this.lastValidatedInstanceId) {
          this.validateDRInstanceBlueprint(drInstance);
        }
      });
  }

  private validateDRInstanceBlueprint(drInstance: any): void {
    if (drInstance.id === this.lastValidatedInstanceId) {
      return;
    }

    this.lastValidatedInstanceId = drInstance.id;

    this.blueprintsService.getBlueprints()
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        (blueprints: any) => {
          const blueprintExists = blueprints.some((bp: any) => bp.id === drInstance.blueprintId);
          if (blueprintExists) {
            this.disasterRecoveryService.setDrValidated(true);
            return;
          }

          const tenantId = this.selectedTenantId || this.tenantId;
          const obj = { tenantId };
          this.instanceService.getInstances(obj)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
              (instances: any) => {
                const drInstancesSorted = this.disasterRecoveryService.getDRInstances(instances.data);

                let validDRInstance = null;
                for (const instance of drInstancesSorted) {
                  const instanceBlueprintExists = blueprints.some((bp: any) => bp.id === instance.blueprintId);
                  if (instanceBlueprintExists) {
                    validDRInstance = instance;
                    break;
                  }
                }

                if (validDRInstance) {
                  this.disasterRecoveryService.updateDrInstance(validDRInstance);
                  this.disasterRecoveryService.setDrValidated(true);
                } else {
                  this.disasterRecoveryService.updateDrInstance(null);
                  this.disasterRecoveryService.setDrValidated(false);
                }
              },
              (error) => {
                this.disasterRecoveryService.updateDrInstance(null);
                this.disasterRecoveryService.setDrValidated(false);
              }
            );
        },
        (error) => {
          this.disasterRecoveryService.updateDrInstance(null);
          this.disasterRecoveryService.setDrValidated(false);
        }
      );
  }

  onViewDrDetails(): void {
    const tenantId = this.selectedTenantId || this.tenantId;
    if (!tenantId) {
      return;
    }

    const triggeredBlueprintId = this.disasterRecoveryService.getTriggeredBlueprintId();
    if (triggeredBlueprintId) {
      this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, tenantId, RouterSegments.DISASTER_RECOVERY, triggeredBlueprintId]);
      return;
    }

    this.disasterRecoveryService.drInstance$
      .pipe(take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(drInstance => {
        if (drInstance?.blueprintId) {
          this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, tenantId, RouterSegments.DISASTER_RECOVERY, drInstance.blueprintId]);
        } else {
          this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, tenantId, RouterSegments.DISASTER_RECOVERY]);
        }
      });
  }
}
